import React, {useEffect, Suspense, lazy} from 'react';
import PropTypes from 'prop-types';
import {Route, Routes, useLocation} from 'react-router-dom';

import {useMarketingAttributionConsumer} from '../../hook/appUrl';
import Loading from '../loading/Loading';

import NotFound from '../../page/notFound/NotFound';
import Home from '../../page/home/Home';
import Product from '../../page/product/Product';
import Search from '../../page/search/Search';
import Brand from '../../page/brand/Brand';
import Seller from '../../page/seller/Seller';

// 첫 진입/핵심 SSR 라우트만 eager import로 유지
const Discover = lazy(() => import('../../page/discover/Discover'));
const BrandRanking = lazy(() => import('../../page/brandRanking/BrandRanking'));
const Download = lazy(() => import('../../page/download/Download'));
const Collection = lazy(() => import('../../page/collection/Collection'));
const Collections = lazy(() => import('../../page/collections/Collections'));
const Login = lazy(() => import('../../page/auth/login/Login.jsx'));
const SignUp = lazy(() => import('../../page/auth/signUp/SignUp'));
const FriendInvite = lazy(() => import('../../page/friendInvite/FriendInvite'));
const SellerCollection = lazy(() => import('../../page/sellerCollection/SellerCollection'));
const Sell = lazy(() => import('../../page/sell/Sell'));
const Fees = lazy(() => import('../../page/fees/Fees'));
const CollectionCortis = lazy(() => import('../../page/collectionCortis/CollectionCortis'));
const SignUpSns = lazy(() => import('../../page/auth/signUp/SignUpSns'));
const SocialAuthComplete = lazy(() => import('../../page/auth/socialAuthComplete/SocialAuthComplete'));
const MyPageWrapper = lazy(() => import('../../page/myPage/MyPageWrapper'));
const Settings = lazy(() => import('../../page/settings/Settings'));
const PurchasedWrapper = lazy(() => import('../../page/purchased/PurchasedWrapper'));
const OrderWrapper = lazy(() => import('../../page/order/OrderWrapper'));
const Upload = lazy(() => import('../../page/upload/Upload'));
const CheckoutWrapper = lazy(() => import('../../page/checkout/CheckoutWrapper'));
const RegisterCheckoutShipping = lazy(() => import('../../page/checkout/shipping/RegisterCheckoutShipping'));
const CheckoutComplete = lazy(() => import('../../page/checkout/complete/CheckoutComplete'));
const CheckoutFail = lazy(() => import('../../page/checkout/fail/CheckoutFail').then(m => ({ default: m.CheckoutFail })));
const ErrorPage = lazy(() => import('../../page/errorPage/ErrorPage'));
const BrandRadar = lazy(() => import('../../page/brandRadar/BrandRadar'));

/**
 * Component with contains all the global routes.
 * @param hostname the webapp hostname
 * @param location a location object to be used for matching children elements instead of the current history location
 * @param userAgent the user agent
 * @return {*}
 * @constructor
 */
export default function AppRoutes({ hostname, userAgent }) {
    const { pathname } = useLocation();

    const withSuspense = (element) => (
        <Suspense fallback={<Loading />}>
            {element}
        </Suspense>
    );

    useEffect(() => {
        // scroll to top on root page change (only when pathname changes)
        window.scrollTo(0, 0);
    }, [pathname]);

    // consume marketing attribution from location parameters
    useMarketingAttributionConsumer();

    // build the UI elements
    return (
        <Routes>
            <Route path="/" element={<Home hostname={hostname} />} />
            <Route path="/brand/:brandName/:categoryName?" element={<Brand hostname={hostname}/>} />
            <Route path="/collection/:collectionId/*" element={withSuspense(<Collection hostname={hostname} userAgent={userAgent} />)} />
            <Route path="/collections" element={withSuspense(<Collections hostname={hostname} />)} />
            <Route path="/discover/:discoverTab?/:categoryName?" element={withSuspense(<Discover hostname={hostname} userAgent={userAgent} />)} />
            <Route path="/download" element={withSuspense(<Download hostname={hostname}/>)} />
            <Route path="/sell" element={withSuspense(<Sell hostname={hostname} userAgent={userAgent}/>)} />
            <Route path="/sell/fees" element={withSuspense(<Fees hostname={hostname} userAgent={userAgent}/>)} />
            <Route path="/brand-ranking" element={withSuspense(<BrandRanking hostname={hostname} userAgent={userAgent} />)} />
            <Route path="/brand-radar/:brandName?" element={withSuspense(<BrandRadar hostname={hostname} userAgent={userAgent} />)} />
            <Route path="/product/:productId/*" element={<Product hostname={hostname} userAgent={userAgent} />} />
            <Route path="/upload" element={withSuspense(<Upload hostname={hostname} userAgent={userAgent} />)} />
            <Route path="/seller/:sellerId/:encodedTitle?/:sellerTab?" element={<Seller hostname={hostname} userAgent={userAgent} />} />
            <Route path="/search" element={<Search hostname={hostname}/>} />
            <Route path="/search/:keyword/:categoryName?" element={<Search hostname={hostname}/>} />
            <Route path="/order/purchased" element={withSuspense(<Download hostname={hostname}/>)} />
            <Route path="/order/selling" element={withSuspense(<Download hostname={hostname}/>)} />
            <Route path="/login" element={withSuspense(<Login />)} />
            <Route path="/signUp" element={withSuspense(<SignUp />)} />
            <Route path="/signUpSns" element={withSuspense(<SignUpSns />)} />
            <Route path="/auth/kakao/complete" element={withSuspense(<SocialAuthComplete socialType={'KAKAO'} />)} />
            <Route path="/auth/apple/complete" element={withSuspense(<SocialAuthComplete socialType={'APPLE'}/>)} />
            <Route path="/auth/facebook/complete" element={withSuspense(<SocialAuthComplete socialType={'FACEBOOK'}/>)} />
            <Route path="/my/:sellerTab?" element={withSuspense(<MyPageWrapper hostname={hostname} userAgent={userAgent} />)} />
            <Route path="/my/purchased" element={withSuspense(<PurchasedWrapper hostname={hostname} userAgent={userAgent} />)} />
            <Route path="/my/purchased/:orderId" element={withSuspense(<OrderWrapper hostname={hostname} userAgent={userAgent} />)} />
            <Route path="/my/settings" element={withSuspense(<Settings hostname={hostname} userAgent={userAgent} />)} />

            <Route path="/checkout/:productId" element={withSuspense(<CheckoutWrapper hostname={hostname}/>)} />
            <Route path="/checkout/shipping/:productId" element={withSuspense(<RegisterCheckoutShipping />)} />
            <Route path="/checkout/complete" element={withSuspense(<CheckoutComplete />)} />
            <Route path="/checkout/fail" element={withSuspense(<CheckoutFail />)} />
            <Route path="/event/join/:code" element={withSuspense(<FriendInvite hostname={hostname} />)} />
            <Route path="/vintage-shops/:sellerCollectionId?/*" element={withSuspense(<SellerCollection hostname={hostname} />)} />
            <Route path="/feature/cortis" element={withSuspense(<CollectionCortis />)} />
            <Route path="/temporary-wrong" element={withSuspense(<ErrorPage />)} />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}
AppRoutes.propTypes = {
    hostname: PropTypes.string.isRequired,
    location: PropTypes.object,
    userAgent: PropTypes.string.isRequired
};
